// On-page performance probe. Everything here is inert unless the URL carries
// debug=1, so customers never pay for it — the Apollo timing link is not even
// added to the chain and the panel renders null.
//
// Turn on:  https://app.rushmyfood.com/restaurant-list?debug=1
//           .../restaurant/<slug>?embed=1&restaurant=<slug>&debug=1
// Turn off: ...?debug=0   (or close the tab — the flag lives in sessionStorage)
//
// The flag is read from the query (or an old #/ link's hash) and persisted the
// same way the embed slug is, so it survives redirects that drop the query string.

const STORAGE_KEY = "perfDebug";

function readFlag() {
  const preHash = new URLSearchParams(window.location.search);
  const hash = window.location.hash || "";
  const q = hash.indexOf("?");
  const inHash = new URLSearchParams(q >= 0 ? hash.slice(q + 1) : "");
  return preHash.get("debug") ?? inHash.get("debug");
}

function resolveEnabled() {
  const flag = readFlag();
  try {
    if (flag === "1") {
      sessionStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    if (flag === "0") {
      sessionStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch (e) {
    return flag === "1";
  }
}

// KILL SWITCH — while true the probe is inert regardless of the URL: no timing
// link in the Apollo chain, no x-perf-trace header, and the panel renders null.
// Flip to false to make ?debug=1 work again.
//
// Parked after the 2026-08 restaurant-list investigation, which it answered in
// full. The list took 7.9s to paint; three things were responsible.
//
//   1. The list query ran TWICE per load — the header's SearchDropdown issues
//      the same query as the list screen, and an undeclared `ip: null` variable
//      on one of them split Apollo's deduplication key. FIXED.
//   2. `myOrders` was fetched on the sign-in boot for every page — 1.1MB, 3.7s,
//      1062ms of JS — and starved everything sharing the event loop: the list
//      resolver measured 256ms alone against 6389ms alongside it. FIXED; orders
//      now load only on the screens that show them.
//   3. A ~139ms floor on EVERY statement, proven with a warm `SELECT 1` timed
//      twice back to back with zero new connections — so neither query time nor
//      `pool.min: 0`. DB_HOST points at Railway's public proxy, so each
//      statement crosses the internet. NOT FIXED: it is a dashboard env change
//      to the *.railway.internal host.
//
// Also named and not yet fixed: the menu query is a 105-statement N+1
// (`advanced_member_modifier_groups` ×75), `Restaurant.activePromotionCount`
// runs a COUNT per restaurant, `orders` returns the customer's whole history
// unbounded (441 rows), and the list loads 1433 menu items to deliver 188
// because the zone filter runs after the menus load.
//
// The backend half (utils/perfTrace.js) stays deployed and is equally inert —
// it only opens a trace for requests carrying the header, which nothing sends
// while this is true.
const PROBE_RETIRED = true;

export const perfDebugEnabled = PROBE_RETIRED ? false : resolveEnabled();

const state = {
  ops: [],
  marks: [],
  // Main-thread blocks over 50ms. A slow page is often not a slow server: if
  // the browser is busy parsing a megabyte of JSON or rendering 1400 menu
  // items, nothing paints no matter how quick the query was.
  longTasks: [],
  // Named stretches of client work (filtering, rendering) measured by callers.
  spans: [],
  counters: {},
};
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

export function subscribePerf(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const getPerfState = () => state;

// Called by the Apollo timing link for every operation that reaches the network.
// Cache hits never get here, which is itself worth seeing.
export function recordOp(op) {
  if (!perfDebugEnabled) return;
  state.ops.push(op);
  notify();
}

// A named moment in the boot sequence, in ms since the navigation started.
export function perfMark(name) {
  if (!perfDebugEnabled) return;
  if (state.marks.some((m) => m.name === name)) return; // first occurrence wins
  state.marks.push({ name, at: Math.round(performance.now()) });
  notify();
}

/**
 * Time a stretch of synchronous client work — the search filter, a map over
 * 1400 menu items, a render pass. Repeated calls with the same name fold into
 * one row with a call count, the same way server phases do.
 *
 *   const rows = perfSpan("filter restaurants", () => searchRestaurants(q));
 *
 * Returns fn()'s value untouched and is a pass-through when the probe is off,
 * so it is safe to leave in a render path.
 */
export function perfSpan(name, fn) {
  if (!perfDebugEnabled) return fn();
  const start = performance.now();
  try {
    return fn();
  } finally {
    const ms = performance.now() - start;
    const found = state.spans.find((s) => s.name === name);
    if (found) {
      found.ms += ms;
      found.calls += 1;
    } else {
      state.spans.push({ name, ms, calls: 1, at: Math.round(start) });
    }
    notify();
  }
}

/** Record a plain count from the client side — rows rendered, items received. */
export function perfCount(key, value) {
  if (!perfDebugEnabled) return;
  state.counters[key] = value;
  notify();
}

// The resource buffer holds 250 entries by default. A restaurant list is a
// photograph per card plus chunks plus fonts, so the GraphQL entries this probe
// needs can be evicted before anyone reads them.
if (perfDebugEnabled) {
  try {
    performance.setResourceTimingBufferSize(1000);
  } catch (e) {
    // Not fatal — the panel just loses some connection breakdowns.
  }
}

// Long tasks are only observable while they are happening, so the observer has
// to exist from module load. `buffered: true` picks up the ones that fired
// during bundle parse, before this line ran.
if (perfDebugEnabled && typeof PerformanceObserver !== "undefined") {
  try {
    const obs = new PerformanceObserver((list) => {
      list.getEntries().forEach((e) => {
        state.longTasks.push({
          at: Math.round(e.startTime),
          ms: Math.round(e.duration),
          source: e.attribution?.[0]?.containerName || e.attribution?.[0]?.name || null,
        });
      });
      notify();
    });
    obs.observe({ type: "longtask", buffered: true });
  } catch (e) {
    // Firefox and Safari do not implement longtask. Everything else still works.
  }

  // Largest contentful paint — the moment the customer would say it loaded,
  // which on this page is a restaurant card, not the header. Observed rather
  // than read back, because LCP entries are not kept in the default buffer.
  try {
    const lcpObs = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      state.lcp = entries[entries.length - 1].startTime;
      notify();
    });
    lcpObs.observe({ type: "largest-contentful-paint", buffered: true });
  } catch (e) {
    // Not supported outside Chromium; the panel just shows "—".
  }
}

const ms = (n) => (n == null ? "—" : `${Math.round(n)}ms`);
const kb = (n) => (n == null ? "—" : `${(n / 1024).toFixed(0)}KB`);

// Splits one operation's wall time into the two halves that need different
// fixes: time the server was busy, and everything else (DNS/TLS/queueing on the
// way out, transfer and JSON parse on the way back).
export function splitOp(op) {
  const serverMs = op.server?.totalMs ?? null;
  return {
    serverMs,
    networkMs: serverMs == null ? null : Math.max(0, op.ms - serverMs),
  };
}

/**
 * Match each recorded operation to the browser's own timing entry for that
 * POST, which is the only place the connection is broken down: how long the
 * request sat queued, DNS, TCP, TLS, time-to-first-byte, and how long the body
 * took to arrive once it started.
 *
 * Matching is greedy-nearest on start time with each entry claimed once, since
 * several operations fire within a few milliseconds of each other on boot.
 *
 * Needs `Timing-Allow-Origin` from the API (the backend sends it) — without it
 * the browser zeroes every field on a cross-origin request, which is reported
 * as `blocked` rather than as a suspiciously fast request.
 */
function enrichOps() {
  // An entry appears once the response has fully arrived, which is normally
  // before the Apollo link records the operation — but not always. Retry a few
  // times rather than writing off a request that was simply a frame early.
  const pending = state.ops.filter((o) => o.net === undefined || (o.net === null && (o._netTries || 0) < 5));
  if (!pending.length) return;

  const claimed = new Set(state.ops.map((o) => o.net?.key).filter(Boolean));
  const entries = performance
    .getEntriesByType("resource")
    .filter((r) => /graphql/i.test(r.name))
    .filter((r) => !claimed.has(`${r.startTime}`));

  pending.forEach((op) => {
    let best = null;
    let bestDelta = Infinity;
    entries.forEach((r) => {
      if (claimed.has(`${r.startTime}`)) return;
      const delta = Math.abs(r.startTime - op.startedAt);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = r;
      }
    });
    // More than a quarter second apart is a different request, not this one.
    if (!best || bestDelta > 250) {
      op.net = null;
      op._netTries = (op._netTries || 0) + 1;
      return;
    }
    claimed.add(`${best.startTime}`);

    // responseStart stays 0 when the server withheld Timing-Allow-Origin.
    if (!best.responseStart) {
      op.net = { key: `${best.startTime}`, blocked: true, totalMs: Math.round(best.duration) };
      return;
    }

    op.net = {
      key: `${best.startTime}`,
      blocked: false,
      // Time between the app asking and the browser actually sending: the
      // per-host connection limit shows up here when six queries fire at once.
      stallMs: Math.max(0, best.requestStart - best.startTime),
      dnsMs: Math.max(0, best.domainLookupEnd - best.domainLookupStart),
      tcpMs: Math.max(0, best.connectEnd - best.connectStart),
      tlsMs: best.secureConnectionStart ? Math.max(0, best.connectEnd - best.secureConnectionStart) : 0,
      // The server thinking, as the browser sees it.
      ttfbMs: Math.max(0, best.responseStart - best.requestStart),
      // The body arriving. Large here means the payload is the problem.
      downloadMs: Math.max(0, best.responseEnd - best.responseStart),
      wireBytes: best.encodedBodySize || 0,
      jsonBytes: best.decodedBodySize || 0,
      // No gzip on a 1MB JSON response is a one-line server fix worth seconds.
      compressed: best.encodedBodySize > 0 && best.decodedBodySize > best.encodedBodySize * 1.1,
      totalMs: Math.round(best.duration),
    };
  });
}

// The operations the restaurant list actually waits on, in the order they fired.
export const listOps = () => {
  enrichOps();
  return state.ops.slice().sort((a, b) => a.startedAt - b.startedAt);
};

/**
 * Was the page waiting on one request at a time, or on all of them at once?
 *
 * Merges the in-flight intervals: `busyMs` is wall time with at least one
 * request outstanding, and the largest gap is dead air where the network was
 * idle and the browser was doing something else (or waiting for a `skip:` to
 * flip). On this page the gap between mount and "location ready" lands here.
 */
export function analyzeWaterfall() {
  const ops = listOps();
  if (!ops.length) return null;
  const spans = ops
    .map((o) => ({ start: o.startedAt, end: o.startedAt + o.ms, name: o.name }))
    .sort((a, b) => a.start - b.start);

  let busy = 0;
  let curStart = spans[0].start;
  let curEnd = spans[0].end;
  let lastName = spans[0].name;
  let largestGap = null;

  for (let i = 1; i < spans.length; i += 1) {
    const s = spans[i];
    if (s.start > curEnd) {
      busy += curEnd - curStart;
      const gap = { ms: Math.round(s.start - curEnd), after: lastName, before: s.name, at: Math.round(curEnd) };
      if (!largestGap || gap.ms > largestGap.ms) largestGap = gap;
      curStart = s.start;
    }
    if (s.end > curEnd) {
      curEnd = s.end;
      lastName = s.name;
    }
  }
  busy += curEnd - curStart;

  return {
    firstStart: Math.round(spans[0].start),
    lastEnd: Math.round(curEnd),
    busyMs: Math.round(busy),
    idleMs: Math.round(Math.max(0, curEnd - spans[0].start - busy)),
    largestGap,
    // Sum of durations well above busy time means they overlapped, which is
    // what we want; close to busy time means they were serialised.
    serialMs: Math.round(ops.reduce((a, o) => a + o.ms, 0)),
  };
}

export function readNavigation() {
  const nav = performance.getEntriesByType("navigation")[0];
  if (!nav) return null;
  return {
    ttfb: nav.responseStart,
    htmlDone: nav.responseEnd,
    domInteractive: nav.domInteractive,
    domContentLoaded: nav.domContentLoadedEventEnd,
    load: nav.loadEventEnd || null,
  };
}

export function readPaint() {
  const fcp = performance.getEntriesByName("first-contentful-paint")[0];
  return {
    fcp: fcp ? fcp.startTime : null,
    // Filled in by the observer above. What the customer would call "it
    // loaded" — usually a restaurant card, not the header.
    lcp: state.lcp ?? null,
  };
}

// The JS/CSS the browser had to download and compile before React could run.
export function readAssets() {
  return performance
    .getEntriesByType("resource")
    .filter((r) => /\.(js|css)$/.test((r.name || "").split("?")[0]))
    .map((r) => ({
      name: (r.name || "").split("/").pop(),
      bytes: r.transferSize || r.encodedBodySize || 0,
      ms: r.duration,
      cached: r.transferSize === 0 && r.decodedBodySize > 0,
    }))
    .sort((a, b) => b.bytes - a.bytes);
}

/**
 * The restaurant cards are photographs, and on this page there is one per
 * restaurant at full size. If images dominate the byte count then the list
 * "feels" slow long after the data arrived.
 */
export function readImages() {
  const imgs = performance
    .getEntriesByType("resource")
    .filter((r) => r.initiatorType === "img" || /\.(png|jpe?g|webp|avif|gif|svg)($|\?)/i.test(r.name || ""));
  return {
    count: imgs.length,
    bytes: imgs.reduce((a, r) => a + (r.transferSize || r.encodedBodySize || 0), 0),
    slowest: imgs
      .slice()
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 3)
      .map((r) => ({ name: (r.name || "").split("/").pop().slice(0, 30), ms: Math.round(r.duration) })),
  };
}

export function readDevice() {
  const c = navigator.connection || {};
  const mem = performance.memory;
  return {
    cores: navigator.hardwareConcurrency || null,
    memory: navigator.deviceMemory || null,
    net: c.effectiveType || null,
    downlink: c.downlink || null,
    rtt: c.rtt || null,
    embedded: window.self !== window.top,
    // Chrome only. A heap climbing past a couple of hundred MB on a list page
    // is the 1400-item search payload sitting in the Apollo cache.
    heapMb: mem ? Math.round(mem.usedJSHeapSize / 1048576) : null,
  };
}

// Plain-text version of everything on screen, for the Copy button — a screenshot
// can crop, this cannot.
export function buildReport() {
  const nav = readNavigation();
  const paint = readPaint();
  const dev = readDevice();
  const assets = readAssets();
  const images = readImages();
  const flow = analyzeWaterfall();
  const jsBytes = assets.reduce((a, r) => a + r.bytes, 0);
  const lines = [];

  lines.push("=== RushMyFood perf probe ===");
  lines.push(`url: ${window.location.href}`);
  lines.push(
    `device: ${dev.cores || "?"} cores, ${dev.memory || "?"}GB, net ${dev.net || "?"}` +
      `${dev.downlink ? ` ${dev.downlink}Mbps` : ""}${dev.rtt ? ` rtt ${dev.rtt}ms` : ""}` +
      `${dev.heapMb ? `, heap ${dev.heapMb}MB` : ""}${dev.embedded ? ", inside iframe" : ""}`
  );
  lines.push(`ua: ${navigator.userAgent}`);
  lines.push("");

  if (nav) {
    lines.push("--- document ---");
    lines.push(`html ttfb          ${ms(nav.ttfb)}`);
    lines.push(`html done          ${ms(nav.htmlDone)}`);
    lines.push(`dom interactive    ${ms(nav.domInteractive)}`);
    lines.push(`dom content loaded ${ms(nav.domContentLoaded)}`);
    lines.push(`load event         ${ms(nav.load)}`);
  }
  if (paint.fcp) lines.push(`first paint        ${ms(paint.fcp)}`);
  if (paint.lcp) lines.push(`largest paint      ${ms(paint.lcp)}`);
  lines.push("");

  lines.push(`--- assets (${kb(jsBytes)} js/css, ${kb(images.bytes)} in ${images.count} images) ---`);
  assets.slice(0, 8).forEach((a) => {
    lines.push(`${kb(a.bytes).padStart(7)}  ${ms(a.ms).padStart(7)}  ${a.cached ? "cached " : "       "}${a.name}`);
  });
  images.slowest.forEach((i) => lines.push(`${"  img".padStart(7)}  ${ms(i.ms).padStart(7)}          ${i.name}`));
  lines.push("");

  if (state.marks.length) {
    lines.push("--- app milestones (since navigation start) ---");
    state.marks
      .slice()
      .sort((a, b) => a.at - b.at)
      .forEach((m) => lines.push(`${ms(m.at).padStart(8)}  ${m.name}`));
    lines.push("");
  }

  if (state.spans.length || Object.keys(state.counters).length) {
    lines.push("--- client work ---");
    state.spans
      .slice()
      .sort((a, b) => b.ms - a.ms)
      .forEach((s) => lines.push(`${ms(s.ms).padStart(8)}  ${s.name}${s.calls > 1 ? ` ×${s.calls}` : ""}`));
    Object.entries(state.counters).forEach(([k, v]) => lines.push(`${String(v).padStart(8)}  ${k}`));
    lines.push("");
  }

  if (state.longTasks.length) {
    const total = state.longTasks.reduce((a, t) => a + t.ms, 0);
    lines.push(`--- main thread blocked: ${state.longTasks.length} long tasks, ${ms(total)} total ---`);
    state.longTasks
      .slice()
      .sort((a, b) => b.ms - a.ms)
      .slice(0, 6)
      .forEach((t) => lines.push(`${ms(t.ms).padStart(8)}  at ${ms(t.at)}${t.source ? `  ${t.source}` : ""}`));
    lines.push("");
  }

  if (flow) {
    lines.push("--- request waterfall ---");
    lines.push(`first request      ${ms(flow.firstStart)}`);
    lines.push(`last response      ${ms(flow.lastEnd)}`);
    lines.push(`network busy       ${ms(flow.busyMs)}  (sum of durations ${ms(flow.serialMs)} — higher means they overlapped)`);
    lines.push(`network idle       ${ms(flow.idleMs)}`);
    if (flow.largestGap) {
      lines.push(
        `largest dead gap   ${ms(flow.largestGap.ms)} at ${ms(flow.largestGap.at)} — after ${flow.largestGap.after}, before ${flow.largestGap.before}`
      );
    }
    lines.push("");
  }

  lines.push(`--- graphql: ${state.ops.length} network ops ---`);
  listOps().forEach((o) => {
    const { serverMs, networkMs } = splitOp(o);
    lines.push(
      `start ${ms(o.startedAt).padStart(7)}  took ${ms(o.ms).padStart(7)}  ${kb(o.bytes).padStart(7)}  ${o.name}${o.error ? "  [ERROR]" : ""}`
    );
    if (serverMs != null) {
      lines.push(
        `        server ${ms(serverMs)} · network+parse ${ms(networkMs)} · ` +
          `sql ${o.server.sqlCount} queries / ${ms(o.server.sqlMs)} · ` +
          (o.server.sqlOverlapped ? "queries overlapped" : `js ${ms(o.server.cpuMs)}`)
      );
    }
    if (o.net && !o.net.blocked) {
      lines.push(
        `        stalled ${ms(o.net.stallMs)} · dns ${ms(o.net.dnsMs)} · tcp ${ms(o.net.tcpMs)} · tls ${ms(o.net.tlsMs)} · ` +
          `ttfb ${ms(o.net.ttfbMs)} · download ${ms(o.net.downloadMs)} · wire ${kb(o.net.wireBytes)} ` +
          `${o.net.compressed ? "(gzip)" : "(NOT COMPRESSED)"} · json ${kb(o.net.jsonBytes)}`
      );
    } else if (o.net?.blocked) {
      lines.push(`        connection breakdown unavailable (no Timing-Allow-Origin on this deploy)`);
    }
  });
  const slowest = state.ops.slice().sort((a, b) => b.ms - a.ms)[0];
  if (slowest) lines.push(`slowest: ${slowest.name} at ${ms(slowest.ms)}`);
  lines.push("");

  // The server-side breakdown for every op that returned one. This is the part
  // that names the culprit, so it goes in full — no truncation.
  listOps()
    .filter((o) => o.server)
    .forEach((o) => {
      const s = o.server;
      lines.push(`--- server phases: ${o.name} (${ms(s.totalMs)} total) ---`);

      if (s.db) {
        lines.push(`  database endpoint: ${s.db.endpoint} :${s.db.port}`);
        (s.db.pings || []).forEach((p) =>
          lines.push(`  bare SELECT 1 (${p.label}): ${p.ms == null ? p.error : ms(p.ms)}`)
        );
        if (s.db.floorMs != null) {
          lines.push(
            `  per-statement floor ${ms(s.db.floorMs)} × ${s.sqlCount} statements = ${ms(s.db.tollMs)} of the ${ms(s.totalMs)}`
          );
        }
        lines.push(`  new connections opened this request: ${s.db.connectionsOpened} (${ms(s.db.connectMs)})`);
        const pb = s.db.poolBefore;
        const pa = s.db.poolAfter;
        if (pb) lines.push(`  pool at start: size ${pb.size}/${pb.max} · idle ${pb.available} · in use ${pb.using} · waiting ${pb.waiting}`);
        if (pa) lines.push(`  pool at end:   size ${pa.size}/${pa.max} · idle ${pa.available} · in use ${pa.using} · waiting ${pa.waiting}`);
      }

      if (s.timeline) {
        lines.push(
          `  waiting on database ${ms(s.timeline.dbBusyMs)} · everything else ${ms(s.timeline.nonDbMs)}`
        );
        (s.timeline.gaps || []).slice(0, 4).forEach((g) =>
          lines.push(`    gap ${ms(g.ms)} at ${ms(g.at)} — after ${g.after}, before ${g.before}${g.phase ? ` [${g.phase}]` : ""}`)
        );
      }

      if (s.gql) {
        lines.push(
          `  graphql parse ${ms(s.gql.parseMs)} · validate ${ms(s.gql.validateMs)} · execute ${ms(s.gql.executeMs)} · ` +
            `serialize ${ms(s.gql.serializeMs)} for ${kb(s.gql.responseBytes)}`
        );
      }
      lines.push("");

      (s.phases || []).forEach((p) => {
        const each = p.calls > 1 ? `  ×${p.calls} (${ms(p.msPerCall)} each)` : "";
        lines.push(
          `${"  ".repeat(p.depth)}${ms(p.ms).padStart(8)}  ${p.queries} q  ${p.name}${each}`
        );
      });

      if (s.fieldHotspots?.length) {
        lines.push(`  slowest resolvers:`);
        s.fieldHotspots.forEach((f) =>
          lines.push(`  ${ms(f.ms).padStart(8)}  ×${String(f.calls).padEnd(4)} ${f.name}  (${ms(f.msPerCall)} each, ${f.queries} q)`)
        );
      }

      if (s.queries?.length) {
        lines.push(`  sql by shape:`);
        s.queries.forEach((q) => {
          lines.push(
            `  ${ms(q.ms).padStart(8)}  ×${String(q.count).padEnd(4)} ${q.shape}` +
              `${q.count > 1 && q.maxMs != null ? ` (worst ${ms(q.maxMs)})` : ""}${q.phase ? `  [in ${q.phase}]` : ""}`
          );
          if (q.sample) lines.push(`            ${q.sample}`);
        });
      }
      const notes = Object.entries(s.notes || {});
      if (notes.length) {
        lines.push(`  counts: ${notes.map(([k, v]) => `${k}=${v}`).join(", ")}`);
      }
      lines.push("");
    });

  return lines.join("\n");
}
